import { useState, useEffect, useRef } from "react";

// ── Animated skill bar ─────────────────────────────────────────────────────
const AnimatedBar = ({ level, color, delay = 0 }) => {
  const [width, setWidth] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTimeout(() => setWidth(level), delay); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [level, delay]);

  return (
    <div ref={ref} style={{ height: 4, background: "var(--gh-border)", borderRadius: 2, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${width}%`, background: color, borderRadius: 2, transition: "width 1s cubic-bezier(0.4,0,0.2,1)" }} />
    </div>
  );
};

// ── Professional Summary (replaces Pinned) ─────────────────────────────────
const ProfessionalSummary = ({ onNavigate }) => {
  const highlights = [
    { icon: "⚛️", title: "React Frontend", desc: "2.5+ years building production-grade React.js apps with clean component architecture, hooks, and state management." },
    { icon: "🐍", title: "Python Backend", desc: "Building backend features with Python, Django, REST APIs, authentication flows, and database-backed workflows." },
    { icon: "🚀", title: "Shipped 8 Projects", desc: "From PDF utilities to SaaS dashboards and Web3 dApps — each project demonstrates ownership from architecture to deployment." },
    { icon: "🤝", title: "Team Player", desc: "Collaborated with senior devs, managed 100+ person society, worked in agile sprints with JIRA and code reviews." },
  ];

  const techStack = [
    { name: "React.js", level: 90, color: "#61dafb" },
    { name: "JavaScript", level: 85, color: "#f1e05a" },
    { name: "Python", level: 75, color: "#3572A5" },
    { name: "Django", level: 70, color: "#44b78b" },
    { name: "Tailwind CSS", level: 85, color: "#06b6d4" },
    { name: "MySQL", level: 65, color: "#4479A1" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Hero card */}
      <div style={{
        background: "linear-gradient(135deg, var(--gh-surface) 0%, var(--gh-bg) 60%, #0e4429 100%)",
        border: "1px solid var(--gh-border)",
        borderRadius: 8, padding: "28px 32px",
        position: "relative", overflow: "hidden",
      }}>
        {/* Decorative background text */}
        <div style={{
          position: "absolute", right: 24, top: "50%", transform: "translateY(-50%)",
          fontSize: 120, fontWeight: 900, color: "rgba(88,166,255,0.04)",
          fontFamily: "monospace", userSelect: "none", pointerEvents: "none",
          lineHeight: 1,
        }}>
          {"</>"}
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <span className="gh-badge gh-badge-green animate-pulse-green" style={{ fontSize: 13 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--gh-green)", display: "inline-block" }} />
                Open to opportunities
              </span>
              <span className="gh-badge gh-badge-blue" style={{ fontSize: 11 }}>Delhi, India · Remote OK</span>
            </div>

            <h1 style={{ fontSize: 28, fontWeight: 700, color: "var(--gh-text)", marginBottom: 6, lineHeight: 1.2 }}>
              Akansha Bharti
            </h1>
            <p style={{ fontSize: 16, color: "var(--gh-link)", fontWeight: 600, marginBottom: 12 }}>
              Full Stack Developer · React · Python · Django
            </p>
            <p style={{ fontSize: 14, color: "var(--gh-text-secondary)", lineHeight: 1.7, maxWidth: 540, marginBottom: 16 }}>
              Full Stack Developer with <strong style={{ color: "var(--gh-text)" }}>2.5+ years of frontend experience</strong> building scalable, responsive web applications. Currently at{" "}
              <a href="https://beyondtax.co/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gh-link)", textDecoration: "none" }}>
                Beyondtax
              </a>
              , delivering React-based features in a fast-paced product team while growing into full-stack development with Python, Django, APIs, and database-driven flows. I bring a sharp eye for design, clean code habits, and a knack for turning complex requirements into intuitive user experiences.
            </p>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                onClick={() => onNavigate("projects")}
                className="btn-gh-green"
                style={{ fontSize: 13 }}
              >
                <svg height="14" viewBox="0 0 16 16" width="14" style={{ fill: "currentColor" }}>
                  <path d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Zm10.5-1h-8a1 1 0 0 0-1 1v6.708A2.486 2.486 0 0 1 4.5 9h8Z" />
                </svg>
                View Projects
              </button>
              <button
                onClick={() => onNavigate("contact")}
                className="btn-gh"
                style={{ fontSize: 13 }}
              >
                <svg height="14" viewBox="0 0 16 16" width="14" style={{ fill: "currentColor" }}>
                  <path d="M1.75 2h12.5c.966 0 1.75.784 1.75 1.75v8.5A1.75 1.75 0 0 1 14.25 14H1.75A1.75 1.75 0 0 1 0 12.25v-8.5C0 2.784.784 2 1.75 2ZM1.5 12.251c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25V5.809L8.38 9.397a.75.75 0 0 1-.761 0L1.5 5.809v6.442Zm13-8.181v-.32a.25.25 0 0 0-.25-.25H1.75a.25.25 0 0 0-.25.25v.32L8 7.88Z" />
                </svg>
                Hire Me
              </button>
              <a
                href="https://github.com/akanshaBharti"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gh"
                style={{ fontSize: 13 }}
              >
                <svg height="14" viewBox="0 0 16 16" width="14" style={{ fill: "currentColor" }}>
                  <path d="M8 0c4.42 0 8 3.58 8 8a8.013 8.013 0 0 1-5.45 7.59c-.4.08-.55-.17-.55-.38 0-.27.01-1.13.01-2.2 0-.75-.25-1.23-.54-1.48 1.78-.2 3.65-.88 3.65-3.95 0-.88-.31-1.59-.82-2.15.08-.2.36-1.02-.08-2.12 0 0-.67-.22-2.2.82-.64-.18-1.32-.27-2-.27-.68 0-1.36.09-2 .27-1.53-1.03-2.2-.82-2.2-.82-.44 1.1-.16 1.92-.08 2.12-.51.56-.82 1.28-.82 2.15 0 3.06 1.86 3.75 3.64 3.95-.23.2-.44.55-.51 1.07-.46.21-1.61.55-2.33-.66-.15-.24-.6-.83-1.23-.82-.67.01-.27.38.01.53.34.19.73.9.82 1.13.16.45.68 1.31 2.69.94 0 .67.01 1.3.01 1.49 0 .21-.15.45-.55.38A7.995 7.995 0 0 1 0 8c0-4.42 3.58-8 8-8Z" />
                </svg>
                GitHub
              </a>
            </div>
          </div>

          {/* Stats column */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, minWidth: 200 }}>
            {[
              { value: "2.5+", label: "Years Exp", color: "var(--gh-green)" },
              { value: "8", label: "Projects", color: "var(--gh-link)" },
              { value: "14+", label: "Technologies", color: "var(--gh-purple)" },
              { value: "24h", label: "Response", color: "var(--gh-yellow)" },
            ].map(s => (
              <div key={s.label} style={{
                background: "var(--gh-card-translucent)", border: "1px solid var(--gh-border)",
                borderRadius: 6, padding: "12px 14px", textAlign: "center",
              }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: "var(--gh-text-secondary)", marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why hire me — 4 highlight cards */}
      <div>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--gh-text)", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <svg height="16" viewBox="0 0 16 16" width="16" style={{ fill: "var(--gh-text-secondary)" }}>
            <path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.75.75 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.873 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25Z" />
          </svg>
          Why work with me
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
          {highlights.map((h, i) => (
            <div
              key={i}
              style={{
                background: "var(--gh-surface)", border: "1px solid var(--gh-border)", borderRadius: 6,
                padding: "16px", transition: "border-color 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = "var(--gh-link)";
                e.currentTarget.style.boxShadow = "0 0 0 1px var(--gh-link-shadow)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = "var(--gh-border)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div style={{ fontSize: 24, marginBottom: 10 }}>{h.icon}</div>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--gh-text)", marginBottom: 6 }}>{h.title}</h3>
              <p style={{ fontSize: 13, color: "var(--gh-text-secondary)", lineHeight: 1.6, margin: 0 }}>{h.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Current role */}
      <div style={{ background: "var(--gh-surface)", border: "1px solid var(--gh-border)", borderRadius: 6, padding: "16px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--gh-text)", display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
            <svg height="16" viewBox="0 0 16 16" width="16" style={{ fill: "var(--gh-text-secondary)" }}>
              <path d="M1.5 3.25a2.25 2.25 0 1 1 3 2.122v5.256a2.251 2.251 0 1 1-1.5 0V5.372A2.25 2.25 0 0 1 1.5 3.25Zm5.677-.177L9.573.677A.25.25 0 0 1 10 .854V2.5h1A2.5 2.5 0 0 1 13.5 5v5.628a2.251 2.251 0 1 1-1.5 0V5a1 1 0 0 0-1-1h-1v1.646a.25.25 0 0 1-.427.177L7.177 3.427a.25.25 0 0 1 0-.354ZM3.75 2.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm0 9.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm8.25.75a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0Z" />
            </svg>
            Current Role
          </h2>
          <span className="gh-badge gh-badge-green">
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--gh-green)", display: "inline-block" }} />
            Active
          </span>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 10 }}>
          <div style={{ width: 40, height: 40, background: "var(--gh-card)", border: "1px solid var(--gh-border)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
            🏢
          </div>
          <div>
            <p style={{ fontSize: 15, fontWeight: 600, color: "var(--gh-text)", margin: 0 }}>
              Frontend Developer (React.js)
              <span style={{ fontSize: 12, fontWeight: 400, color: "var(--gh-text-secondary)", marginLeft: 8 }}>· Full-time</span>
            </p>
            <p style={{ fontSize: 13, color: "var(--gh-link)", margin: "2px 0 0" }}>
              <a href="https://beyondtax.co/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gh-link)", textDecoration: "none" }}>Beyondtax</a>
              <span style={{ color: "var(--gh-text-secondary)" }}> · June 2024 – Present</span>
            </p>
          </div>
        </div>
        <ul style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
          {[
            "Develop & maintain React.js features for a tax-tech platform",
            "Collaborate with design and backend teams on end-to-end feature delivery",
            "Apply Python, Django, and API knowledge through full-stack project work",
          ].map((item, i) => (
            <li key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: "var(--gh-text-secondary)", marginBottom: 6 }}>
              <span style={{ color: "var(--gh-green)", flexShrink: 0 }}>▸</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
          {["React.js", "JavaScript", "Tailwind CSS", "Python", "Django", "Git"].map(t => (
            <span key={t} className="gh-topic" style={{ fontSize: 11 }}>{t}</span>
          ))}
        </div>
      </div>

      {/* Quick tech stack */}
      <div style={{ background: "var(--gh-surface)", border: "1px solid var(--gh-border)", borderRadius: 6, padding: "16px 20px" }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--gh-text)", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
          <svg height="16" viewBox="0 0 16 16" width="16" style={{ fill: "var(--gh-text-secondary)" }}>
            <path d="M0 1.75C0 .784.784 0 1.75 0h12.5C15.216 0 16 .784 16 1.75v9.5A1.75 1.75 0 0 1 14.25 13H8.06l-2.573 2.573A1.458 1.458 0 0 1 3 14.543V13H1.75A1.75 1.75 0 0 1 0 11.25Zm1.75-.25a.25.25 0 0 0-.25.25v9.5c0 .138.112.25.25.25h2a.75.75 0 0 1 .75.75v2.19l2.72-2.72a.749.749 0 0 1 .53-.22h6.5a.25.25 0 0 0 .25-.25v-9.5a.25.25 0 0 0-.25-.25Z" />
          </svg>
          Tech Stack at a Glance
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
          {techStack.map((skill, i) => (
            <div key={skill.name} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "var(--gh-text)", display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: skill.color, display: "inline-block" }} />
                  {skill.name}
                </span>
                <span style={{ fontSize: 11, color: "var(--gh-text-secondary)" }}>{skill.level}%</span>
              </div>
              <AnimatedBar level={skill.level} color={skill.color} delay={i * 80} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── README Section ─────────────────────────────────────────────────────────
const ReadmeSection = () => {
  const ref = useRef(null);

  return (
    <div ref={ref} style={{ background: "var(--gh-surface)", border: "1px solid var(--gh-border)", borderRadius: 6, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderBottom: "1px solid var(--gh-border)", background: "var(--gh-card)" }}>
        <svg aria-hidden="true" height="16" viewBox="0 0 16 16" width="16" style={{ fill: "var(--gh-text-secondary)" }}>
          <path d="M0 1.75A.75.75 0 0 1 .75 1h4.253c1.227 0 2.317.59 3 1.501A3.743 3.743 0 0 1 11.006 1h4.245a.75.75 0 0 1 .75.75v10.5a.75.75 0 0 1-.75.75h-4.507a2.25 2.25 0 0 0-1.591.659l-.622.621a.75.75 0 0 1-1.063 0L7.147 13.66A2.25 2.25 0 0 0 5.557 13H.75a.75.75 0 0 1-.75-.75Zm7.251 10.324.004-5.073-.002-2.253A2.25 2.25 0 0 0 5.003 2.5H1.5v9h4.057a3.75 3.75 0 0 1 1.694.824Zm1.502-7.327-.003 2.254.004 5.073a3.75 3.75 0 0 1 1.694-.824H15.5v-9h-3.495a2.25 2.25 0 0 0-2.252 2.247Z" />
        </svg>
        <span style={{ fontSize: 14, color: "var(--gh-text)", fontWeight: 600 }}>README.md</span>
      </div>
      <div className="readme-body" style={{ padding: "24px 32px", lineHeight: 1.7 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, borderBottom: "1px solid var(--gh-border)", paddingBottom: 12, marginBottom: 16 }}>
          Hi there, I'm Akansha 👋
        </h1>
        <p style={{ color: "var(--gh-text)", marginBottom: 20 }}>
          Passionate <strong style={{ color: "var(--gh-link)" }}>Full Stack Developer</strong> working with React.js, Python, and Django. I love turning ideas into polished digital experiences from interface to backend flow.
        </p>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10, color: "var(--gh-text)" }}>🚀 What I'm up to</h2>
        <ul style={{ paddingLeft: 20, color: "var(--gh-text-secondary)", marginBottom: 20 }}>
          {[
            "🏢 Working as Frontend Developer at Beyondtax (React.js) — June 2024 to Present",
            "🌱 Exploring advanced React patterns, Django APIs, performance optimisation, and Web3",
            "🎯 Open to exciting Full Stack Developer opportunities",
            "📍 Based in Delhi, India — open to remote work",
          ].map((item, i) => (
            <li key={i} style={{ marginBottom: 6 }}><span style={{ color: "var(--gh-text)" }}>{item}</span></li>
          ))}
        </ul>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10, color: "var(--gh-text)" }}>📫 Reach me</h2>
        <p style={{ color: "var(--gh-text-secondary)" }}>
          <a href="mailto:akanshabharti1213@gmail.com" style={{ color: "var(--gh-link)" }}>akanshabharti1213@gmail.com</a>
          {" · "}
          <a href="https://www.linkedin.com/in/akansha-bharti/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gh-link)" }}>LinkedIn</a>
          {" · "}
          <a href="https://github.com/akanshaBharti" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gh-link)" }}>GitHub</a>
        </p>
      </div>
    </div>
  );
};

// ── Home (Overview) ────────────────────────────────────────────────────────
const Home = ({ onNavigate }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
    <ProfessionalSummary onNavigate={onNavigate} />
    {/* <ContributionGraph /> */}
    <ReadmeSection />
    <style>{`
      @media (max-width: 640px) {
        .readme-body { padding: 16px !important; }
      }
    `}</style>
  </div>
);

export default Home;
